import { CEEDERO_API_ORIGIN } from "@/api/client";
import { createTrackDuplicateKey } from "@/music-catalog/utils/track-identity";

import type { CloudLibrary, LibraryProject } from "@/cloud/types/types";
import type { AppLanguage } from "@/localization/hooks/use-translation";

/**
 * What the Music catalog shows, decided away from the component.
 *
 * The catalog is tracks indexed from projects, plus standalone tracks added by
 * hand — the same two halves the desktop's own Music Catalog names. Reading
 * only the standalone half is why this page was empty beside a desktop showing
 * 215 tracks: a project's tracklist syncs inside the project, and only tracks
 * with no project of their own travel as Catalog Tracks.
 */

export interface CatalogTrackEntry {
  artist: string;
  /** Null when the desktop never read one, which a row must not draw as 0:00. */
  durationSeconds: number | null;
  id: string;
  /** Its place in its project tracklist. Absent on a standalone track. */
  position?: number;
  /** Absent on a standalone track, which belongs to no project. */
  projectId?: string;
  projectTitle?: string;
  title: string;
}

/**
 * A pull reports a removal as a tombstone rather than as an absence, so a
 * removed project arrives in the list and has to be dropped. Drawing one would
 * show a project the user deleted as though it were still stored.
 */
export const selectLiveProjects = (projects: LibraryProject[]) =>
  projects
    .filter((project) => project.deletedAt === null)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));

/** Every track the account holds, from both halves, in one list. */
export const collectCatalogTracks = (library: CloudLibrary): CatalogTrackEntry[] => [
  ...selectLiveProjects(library.projects).flatMap((project) =>
    project.tracks.map((track) => ({
      artist: track.artist,
      durationSeconds: track.durationSeconds,
      id: `${project.desktopProjectId}:${track.id}`,
      position: track.position,
      projectId: project.desktopProjectId,
      projectTitle: project.title,
      title: track.title,
    })),
  ),
  ...library.tracks
    .filter((track) => track.deletedAt === null)
    // A standalone track has no tracklist to hold a position in, and the
    // Catalog Track wire shape carries no duration.
    .map((track) => ({
      artist: track.artist,
      durationSeconds: null,
      id: track.id,
      title: track.title,
    })),
];

/**
 * Every track in one pass, indexed by identity; a key holding more than one
 * entry is a duplicate.
 *
 * One index rather than a question asked per track: the per-track form
 * re-scans the whole catalog for each row and re-normalizes every candidate on
 * the way past, which on the desktop came to tens of thousands of regex-heavy
 * comparisons for a single render of a 200-track album.
 */
export const buildDuplicateGroups = (tracks: CatalogTrackEntry[]) => {
  const groups = new Map<string, CatalogTrackEntry[]>();
  for (const track of tracks) {
    const key = createTrackDuplicateKey(track);
    if (key === null) {
      continue;
    }
    groups.set(key, [...(groups.get(key) ?? []), track]);
  }
  return [...groups.values()].filter((group) => group.length > 1);
};

/**
 * Folded for comparison only, never for display. Diacritics are stripped so
 * somebody typing "ciurlionis" on a keyboard they have not switched finds
 * "Čiurlionis"; the stored value is untouched, because this is a search key and
 * not a correction of the user's own text.
 */
const foldForSearch = (value: string) =>
  value
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

/**
 * Substring rather than fuzzy matching. A fuzzy scorer answers a query with
 * everything, ranked — right for a palette choosing one item, wrong for a
 * filter whose job is to say what matched and what did not.
 */
export const searchCatalog = <Item extends { artist?: string; title: string }>({
  items,
  query,
}: {
  items: Item[];
  query: string;
}) => {
  const folded = foldForSearch(query);
  if (folded.length === 0) {
    return items;
  }
  return items.filter(
    (item) =>
      foldForSearch(item.title).includes(folded) ||
      (item.artist !== undefined && foldForSearch(item.artist).includes(folded)),
  );
};

export const countArtists = (tracks: CatalogTrackEntry[]) =>
  new Set(tracks.map((track) => foldForSearch(track.artist)).filter((artist) => artist.length > 0))
    .size;

/**
 * The size of something, in the reader's own language.
 *
 * `Intl` owns the unit names, which is why none is written here: "MB" is not
 * universal, the space before it is not universal either, and a hand-assembled
 * string is a translated sentence built from fragments. Decimal multiples,
 * because the unit names are decimal ones.
 */
const SIZE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;

export const formatByteSize = (bytes: number, language: AppLanguage) => {
  const safeBytes = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  const unitIndex = Math.min(
    SIZE_UNITS.length - 1,
    safeBytes === 0 ? 0 : Math.floor(Math.log10(safeBytes) / 3),
  );

  return new Intl.NumberFormat(language, {
    maximumFractionDigits: unitIndex === 0 ? 0 : 1,
    style: "unit",
    unit: SIZE_UNITS[unitIndex]!,
    unitDisplay: "short",
  }).format(safeBytes / 1000 ** unitIndex);
};

/**
 * Formatted against the application's own language rather than
 * `toLocaleString()`'s default, which follows the operating system.
 */
export const formatCatalogDate = (isoTimestamp: string, language: AppLanguage) => {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(parsed);
};

/**
 * Where an object's bytes are.
 *
 * Addressed by digest, because the head this page already holds carries both
 * digests — the per-project reads cost a lookup to recover what the caller was
 * given, and are superseded for that reason. A plain URL rather than a fetch:
 * the cookie is same-site, so the browser sends it on an image request and a
 * navigation alike.
 */
export const resolveCatalogObjectUrl = ({
  contentSha256,
  kind,
}: {
  contentSha256: string;
  kind: "preview" | "project";
}) => `${CEEDERO_API_ORIGIN}/v1/library/objects/${kind}/${encodeURIComponent(contentSha256)}`;

/** The name a downloaded project takes on the reader's machine. */
export const resolveProjectDownloadName = (project: LibraryProject) => {
  const stem = project.title.trim().replaceAll(/[/:*?"<>|]/g, "-");
  return `${stem.length === 0 ? project.desktopProjectId : stem}.ceedero`;
};

/**
 * Where a project's bytes are, asked for under the name they should be saved
 * as.
 *
 * The name has to travel in the URL, because `<a download>` is ignored on a
 * cross-origin one and the API is a second origin - so the attribute alone left
 * every download named after the last path segment: the 64-character digest,
 * with no `.ceedero` extension. It cannot be caught in development either,
 * where the API is proxied onto this origin and the attribute is honoured. The
 * API answers the query with `Content-Disposition`, which no origin rule
 * touches.
 */
export const resolveProjectDownloadUrl = (project: LibraryProject) => {
  const url = resolveCatalogObjectUrl({
    contentSha256: project.contentSha256,
    kind: "project",
  });
  return `${url}?filename=${encodeURIComponent(resolveProjectDownloadName(project))}`;
};

/** How the grid is ordered. Each is a question somebody actually asks of a list. */
export type CatalogSortOrder = "duration" | "name" | "tracks" | "updated";

export const sortProjects = ({
  order,
  projects,
}: {
  order: CatalogSortOrder;
  projects: LibraryProject[];
}) => {
  const trackCountOf = (project: LibraryProject) => project.trackCount ?? project.tracks.length;
  return [...projects].sort((left, right) => {
    switch (order) {
      case "name": {
        return left.title.localeCompare(right.title);
      }
      case "tracks": {
        return trackCountOf(right) - trackCountOf(left);
      }
      case "duration": {
        return sumTrackDuration(right.tracks).seconds - sumTrackDuration(left.tracks).seconds;
      }
      default: {
        return right.updatedAt.localeCompare(left.updatedAt);
      }
    }
  });
};

/**
 * Which project each duplicate sits in, and how many it has.
 *
 * Counting duplicates without saying where they are is the half that helps
 * nobody: the number tells the user something is wrong and the list is where
 * they would go to fix it. Standalone tracks belong to no project and are
 * counted under no key.
 */
export const countDuplicatesByProject = (groups: CatalogTrackEntry[][]) => {
  const counts = new Map<string, number>();
  for (const group of groups) {
    for (const entry of group) {
      if (entry.projectId !== undefined) {
        counts.set(entry.projectId, (counts.get(entry.projectId) ?? 0) + 1);
      }
    }
  }
  return counts;
};

/** The identity of every track that occurs more than once, for marking a row. */
export const collectDuplicateEntryIds = (groups: CatalogTrackEntry[][]) =>
  new Set(groups.flatMap((group) => group.map((entry) => entry.id)));

/**
 * How long an album runs, from the tracks that report a duration.
 *
 * A track whose duration was never read contributes nothing rather than a zero,
 * and `isComplete` says whether any were missing — a running time quietly short
 * by three tracks is worse than one labelled as partial.
 */
export const sumTrackDuration = (tracks: { durationSeconds: number | null }[]) => {
  const timed = tracks.filter(
    (track): track is { durationSeconds: number } =>
      typeof track.durationSeconds === "number" && Number.isFinite(track.durationSeconds),
  );
  return {
    isComplete: timed.length === tracks.length,
    seconds: timed.reduce((total, track) => total + Math.max(0, track.durationSeconds), 0),
  };
};

/** A single track's own running time, which is read as a clock rather than as prose. */
export const formatTrackDuration = (seconds: number | null) => {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return undefined;
  }
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
};

/**
 * Whether a project answers a search, counting its tracklist as part of it.
 *
 * A project's own title is not the only thing somebody searches for: the
 * desktop's field matches artists and track titles too, and a query that named
 * a song but returned nothing would read as a broken search rather than as one
 * looking only at album names.
 */
export const searchProjects = ({
  projects,
  query,
}: {
  projects: LibraryProject[];
  query: string;
}) => {
  const folded = foldForSearch(query);
  if (folded.length === 0) {
    return projects;
  }
  return projects.filter(
    (project) =>
      foldForSearch(project.title).includes(folded) ||
      project.tracks.some(
        (track) =>
          foldForSearch(track.title).includes(folded) ||
          foldForSearch(track.artist).includes(folded),
      ),
  );
};
